import React, { useState, useEffect } from 'react';
import { useDevice } from '../../context/DeviceContext';

interface DebugInfo {
  userAgent: string;
  screenWidth: number;
  screenHeight: number;
  windowWidth: number;
  windowHeight: number;
  aspectRatio: number;
  pixelRatio: number;
}

/**
 * Componente para depurar la detección de dispositivos
 * Muestra información detallada sobre el dispositivo y permite forzar un tipo de dispositivo
 */
export const DeviceDebug: React.FC = () => {
  const { deviceType, isTablet, isMobile, isDesktop, needsStepByStepInterface, setForceDeviceType } = useDevice();
  const [debugInfo, setDebugInfo] = useState<DebugInfo>({
    userAgent: '',
    screenWidth: 0,
    screenHeight: 0,
    windowWidth: 0,
    windowHeight: 0,
    aspectRatio: 0,
    pixelRatio: 0
  });
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const updateDebugInfo = () => {
      setDebugInfo({
        userAgent: navigator.userAgent,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        aspectRatio: window.innerWidth / window.innerHeight,
        pixelRatio: window.devicePixelRatio || 1
      });
    };

    updateDebugInfo();
    window.addEventListener('resize', updateDebugInfo);
    
    return () => {
      window.removeEventListener('resize', updateDebugInfo);
    };
  }, []);

  const handleForceDeviceType = (type: 'mobile' | 'tablet' | 'desktop') => {
    setForceDeviceType(type, true);
  };

  const clearForcedDevice = () => {
    localStorage.removeItem('preferredDeviceType');
    window.location.reload();
  };

  if (!isExpanded) {
    return (
      <div 
        style={{ 
          position: 'fixed', 
          bottom: '10px', 
          right: '10px', 
          zIndex: 9999,
          backgroundColor: 'rgba(0,0,0,0.7)',
          color: 'white',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          cursor: 'pointer',
          fontSize: '20px'
        }}
        onClick={() => setIsExpanded(true)}
      >
        📱
      </div>
    );
  }

  return (
    <div 
      style={{ 
        position: 'fixed', 
        bottom: '10px', 
        right: '10px', 
        zIndex: 9999,
        backgroundColor: 'rgba(0,0,0,0.85)',
        color: 'white',
        padding: '15px',
        borderRadius: '8px',
        maxWidth: '90%',
        width: '350px',
        maxHeight: '80vh',
        overflowY: 'auto',
        fontSize: '14px',
        fontFamily: 'monospace'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
        <h3 style={{ margin: 0 }}>Depurador de Dispositivo</h3>
        <button 
          onClick={() => setIsExpanded(false)}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'white',
            fontSize: '18px',
            cursor: 'pointer'
          }}
        >
          ✖
        </button>
      </div>

      <div style={{ marginBottom: '15px' }}>
        <h4 style={{ marginBottom: '5px', borderBottom: '1px solid rgba(255,255,255,0.3)' }}>Tipo Detectado:</h4>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '5px' }}>
          <span>Tipo de dispositivo:</span>
          <strong style={{ 
            padding: '3px 8px', 
            borderRadius: '4px', 
            backgroundColor: 
              deviceType === 'mobile' ? '#e91e63' : 
              deviceType === 'tablet' ? '#2196f3' : 
              '#4caf50'
          }}>
            {deviceType}
          </strong>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '5px' }}>
          <span>Usa interfaz paso a paso:</span>
          <strong>{needsStepByStepInterface ? '✅ Sí' : '❌ No'}</strong>
        </div>
      </div>

      <div style={{ marginBottom: '15px' }}>
        <h4 style={{ marginBottom: '5px', borderBottom: '1px solid rgba(255,255,255,0.3)' }}>Información del Dispositivo:</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridGap: '5px' }}>
          <span>Pantalla:</span>
          <span>{debugInfo.screenWidth}×{debugInfo.screenHeight}</span>
          
          <span>Ventana:</span>
          <span>{debugInfo.windowWidth}×{debugInfo.windowHeight}</span>
          
          <span>Ratio de aspecto:</span>
          <span>{debugInfo.aspectRatio.toFixed(2)}</span>
          
          <span>Pixel ratio:</span>
          <span>{debugInfo.pixelRatio}</span>
        </div>
        
        <div style={{ marginTop: '10px', fontSize: '12px', wordBreak: 'break-all' }}>
          <strong>User Agent:</strong><br />
          {debugInfo.userAgent}
        </div>
      </div>

      <div>
        <h4 style={{ marginBottom: '5px', borderBottom: '1px solid rgba(255,255,255,0.3)' }}>Forzar Tipo:</h4>
        <div style={{ display: 'flex', gap: '5px', marginBottom: '10px' }}>
          <button 
            onClick={() => handleForceDeviceType('mobile')}
            style={{
              flex: 1,
              padding: '8px',
              backgroundColor: deviceType === 'mobile' ? '#e91e63' : '#333',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Móvil
          </button>
          <button 
            onClick={() => handleForceDeviceType('tablet')}
            style={{
              flex: 1,
              padding: '8px',
              backgroundColor: deviceType === 'tablet' ? '#2196f3' : '#333',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Tablet
          </button>
          <button 
            onClick={() => handleForceDeviceType('desktop')}
            style={{
              flex: 1,
              padding: '8px',
              backgroundColor: deviceType === 'desktop' ? '#4caf50' : '#333',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Desktop
          </button>
        </div>
        <button 
          onClick={clearForcedDevice}
          style={{
            width: '100%',
            padding: '8px',
            backgroundColor: '#ff9800',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Restablecer Detección Auto
        </button>
      </div>
    </div>
  );
};
